import type { LexicalEditor } from "lexical";
import { useEffect, useRef } from "react";

const hasScrollableOverflow = (overflowY: string) =>
  overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";

const findScrollAncestor = (element: HTMLElement | null): HTMLElement | null => {
  let currentElement: HTMLElement | null = element?.parentElement ?? null;

  while (currentElement) {
    const { overflowY } = window.getComputedStyle(currentElement);
    if (hasScrollableOverflow(overflowY)) {
      return currentElement;
    }
    currentElement = currentElement.parentElement;
  }

  return null;
};

const getScrollTargets = (rootElement: HTMLElement) => {
  const container = rootElement.closest(".editor-container") as HTMLElement | null;
  const scrollAncestor = findScrollAncestor(container ?? rootElement);
  const elementTargets = [rootElement, container, scrollAncestor].filter(
    (target): target is HTMLElement => !!target
  );

  return {
    rootElement,
    container,
    scrollAncestor,
    elementTargets: Array.from(new Set(elementTargets)),
  };
};

type ScrollSource = HTMLElement | Window | null;

export interface EditorScrollEvent {
  rootElement: HTMLElement;
  container: HTMLElement | null;
  scrollAncestor: HTMLElement | null;
  source: ScrollSource;
}

type ScrollSubscriber = (event: EditorScrollEvent) => void;

interface ScrollCoordinator {
  rootElement: HTMLElement;
  container: HTMLElement | null;
  scrollAncestor: HTMLElement | null;
  subscribers: Set<ScrollSubscriber>;
  teardown: () => void;
}

const scrollCoordinatorByRoot = new WeakMap<HTMLElement, ScrollCoordinator>();

const createScrollCoordinator = (rootElement: HTMLElement): ScrollCoordinator => {
  const { container, scrollAncestor, elementTargets } = getScrollTargets(rootElement);
  const subscribers = new Set<ScrollSubscriber>();

  const emitSubscribers = (source: ScrollSource) => {
    const event: EditorScrollEvent = {
      rootElement,
      container,
      scrollAncestor,
      source,
    };

    [...subscribers].forEach((subscriber) => {
      subscriber(event);
    });
  };

  const emit = (source: ScrollSource) => emitSubscribers(source);

  const removeElementListeners = elementTargets.map((target) => {
    const handleScroll = () => {
      emit(target);
    };
    target.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      target.removeEventListener("scroll", handleScroll);
    };
  });

  const handleWindowScroll = (event: Event) => {
    if (
      event.target instanceof HTMLElement &&
      elementTargets.includes(event.target)
    ) {
      return;
    }

    emit(window);
  };

  window.addEventListener("scroll", handleWindowScroll, {
    capture: true,
    passive: true,
  });

  const teardown = () => {
    removeElementListeners.forEach((removeListener) => {
      removeListener();
    });
    window.removeEventListener("scroll", handleWindowScroll, true);
  };

  return {
    rootElement,
    container,
    scrollAncestor,
    subscribers,
    teardown,
  };
};

const getOrCreateCoordinator = (rootElement: HTMLElement) => {
  const existingCoordinator = scrollCoordinatorByRoot.get(rootElement);
  if (existingCoordinator) return existingCoordinator;

  const coordinator = createScrollCoordinator(rootElement);
  scrollCoordinatorByRoot.set(rootElement, coordinator);
  return coordinator;
};

export const subscribeToEditorScroll = (
  rootElement: HTMLElement,
  subscriber: ScrollSubscriber,
  { emitInitial = true }: { emitInitial?: boolean } = {}
) => {
  const coordinator = getOrCreateCoordinator(rootElement);
  coordinator.subscribers.add(subscriber);

  if (emitInitial) {
    subscriber({
      rootElement: coordinator.rootElement,
      container: coordinator.container,
      scrollAncestor: coordinator.scrollAncestor,
      source: null,
    });
  }

  return () => {
    coordinator.subscribers.delete(subscriber);
    if (coordinator.subscribers.size > 0) return;

    coordinator.teardown();
    scrollCoordinatorByRoot.delete(rootElement);
  };
};

export const useEditorScrollSubscription = (
  editor: LexicalEditor,
  onScroll: ScrollSubscriber,
  emitInitial = true
) => {
  const onScrollRef = useRef(onScroll);

  useEffect(() => {
    onScrollRef.current = onScroll;
  }, [onScroll]);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const attachSubscription = (rootElement: HTMLElement | null) => {
      unsubscribe?.();
      unsubscribe = null;

      if (!rootElement) return;

      unsubscribe = subscribeToEditorScroll(
        rootElement,
        (event) => {
          onScrollRef.current(event);
        },
        { emitInitial }
      );
    };

    attachSubscription(editor.getRootElement());

    const unregisterRootListener =
      typeof editor.registerRootListener === "function"
        ? editor.registerRootListener((rootElement) => {
            attachSubscription(rootElement);
          })
        : () => {};

    return () => {
      unsubscribe?.();
      unregisterRootListener();
    };
  }, [editor, emitInitial]);
};

export { findScrollAncestor };
